import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { FormModelStructureInput, FormModelValidationMode, FormStatus, LocalFile } from '@types'

import { Refresh } from '@components/Refresh'

import { useModal } from '@hooks/app/modal.hook'
import { useForm } from '@hooks/form/form.hook'
import { useSaveForm } from '@hooks/form/save-form.hook'
import { useSubmitForm } from '@hooks/form/submit-form.hook'
import { useUpdateStatusForm } from '@hooks/form/update-status-form.hook'
import { useProjectGuards } from '@hooks/project/project-guards.hook'

import { Modal } from '@components/Modal'

import { FormSubmitter } from '../../components/FormSubmitter'

import { useDebounceCallback } from '@utils/hooks'

import { Container } from './submit-form.styled'

export const FormPage = (): JSX.Element => {
  const { projectId } = useParams()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { hideModal } = useModal()

  const formId = searchParams.get('formId')

  const [formSubmitData, setFormSubmitData] = useState<{
    form: FormModelStructureInput
    localFiles: Record<string, LocalFile[]>
  }>()

  const { getProjectGuards } = useProjectGuards({ projectId })
  const { canEditFormValues } = getProjectGuards()

  const { updateStatusForm } = useUpdateStatusForm()
  const { submitForm } = useSubmitForm()
  const { saveForm } = useSaveForm()

  const { data, loading, error, refetch } = useForm(formId || '')

  const debouncedSave = useDebounceCallback(
    (formModel: FormModelStructureInput, localFiles: Record<string, LocalFile[]>) => {
      if (!formId) return

      saveForm(
        {
          formId,
          formModel: {
            fields: formModel.fields,
            validationConfig: {
              mode: formModel.validationConfig.mode,
              allowAdminEdit: formModel.validationConfig.allowAdminEdit,
            },
          },
        },
        localFiles,
      )
    },
    500,
  )

  const handleOnSubmit = () => {
    if (!form) return

    const { mode } = form.formModel.validationConfig

    if (mode == FormModelValidationMode.Submit && formSubmitData) {
      submitForm({
        formId: form.id,
        formModel: {
          fields: formSubmitData.form.fields,
          validationConfig: formSubmitData.form.validationConfig,
        },
      })
    }

    hideModal('submit_form')
  }

  const handleOnFirstInteraction = () => {
    if (formId && form?.status === FormStatus.StatusToDo)
      updateStatusForm({
        formId,
        status: FormStatus.StatusInProgress,
      })
  }

  const handleOnChange = (formModel: FormModelStructureInput, localFiles: Record<string, LocalFile[]>) => {
    setFormSubmitData({ form: formModel, localFiles })
    debouncedSave(formModel, localFiles)
  }

  const form = data?.form
  const isLoading = loading ? 1 : error ? -1 : 0
  const mode = form && canEditFormValues(form) ? 'edit' : 'preview'

  const submitLabel =
    form?.formModel.validationConfig.mode === FormModelValidationMode.Submit
      ? t('modules_project-forms_pages_form_submit_label')
      : t('modules_project-forms_pages_form_submit_save_label')

  const noValidate = form?.formModel.validationConfig.mode === FormModelValidationMode.OnChange

  return (
    <Modal
      title={form?.name || ''}
      onSubmitLabel={mode === 'edit' ? submitLabel : undefined}
      onSubmit={mode === 'edit' ? handleOnSubmit : undefined}
      onClose={() => hideModal('submit_form')}
      noValidate={noValidate}
    >
      <Container>
        <Refresh loading={isLoading} small onRefresh={() => refetch(formId || '')}>
          {form && (
            <FormSubmitter
              formModel={form.formModel}
              mode={mode}
              onFirstInteraction={handleOnFirstInteraction}
              onChange={handleOnChange}
            />
          )}
        </Refresh>
      </Container>
    </Modal>
  )
}
